import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, User, ArrowLeft, LogOut, Send, Loader2, Zap, Menu, X, Route as RouteIcon, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { driverApi } from '../services/api';

const NAV = [
  { to: '/driver', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/driver/available', icon: Zap, label: 'Available orders' },
  { to: '/driver/deliveries', icon: ClipboardList, label: 'My deliveries' },
  { to: '/driver/route', icon: RouteIcon, label: 'My route' },
  { to: '/driver/earnings', icon: Wallet, label: 'Earnings' },
  { to: '/driver/profile', icon: User, label: 'Profile' },
];

const DriverLayout = () => {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [driver, setDriver] = useState(null);
  const [state, setState] = useState('checking'); // checking | none | pending | rejected | ok
  // Mobile drawer state — declared with the other hooks to keep hook order stable.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login'); return; }
    driverApi.me()
      .then((d) => {
        setDriver(d);
        if (d.status === 'approved') setState('ok');
        else setState(d.status === 'rejected' ? 'rejected' : 'pending');
      })
      .catch(() => setState('none'));
  }, [loading, user, nav]);

  if (loading || state === 'checking') return <div className="h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (state === 'none') return <Navigate to="/driver/apply" replace />;
  if (state !== 'ok') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 max-w-md text-center">
          <div className="h-12 w-12 rounded-full bg-amber-50 text-amber-600 mx-auto flex items-center justify-center font-bold">!</div>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">{state === 'rejected' ? 'Application rejected' : 'Awaiting approval'}</h1>
          <p className="text-slate-500 mt-1 text-sm">{state === 'rejected' ? 'Your driver application was rejected.' : 'A platform admin will review your application shortly.'}</p>
          <button onClick={() => nav('/driver/apply')} className="mt-5 px-4 h-10 bg-slate-900 text-white rounded-lg hover:bg-slate-800">View application</button>
        </div>
      </div>
    );
  }

  const initials = (driver?.name || user?.name || 'D').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* Mobile top bar (visible only on <md) */}
      <div className="md:hidden sticky top-0 z-30 bg-slate-900 text-white flex items-center gap-2 px-3 py-2 shadow-sm" data-testid="driver-mobile-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="h-9 w-9 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center"
          aria-label="Open menu"
          data-testid="driver-mobile-menu-open"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="h-7 w-7 rounded-full bg-emerald-600 flex items-center justify-center">
            <Send className="h-3.5 w-3.5 text-white -rotate-12" strokeWidth={2.6} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate leading-tight">ShipitNow</div>
            <div className="text-[9px] uppercase tracking-widest text-emerald-300 leading-none">Driver</div>
          </div>
        </div>
      </div>

      {/* Mobile scrim */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileNavOpen(false)}
          data-testid="driver-mobile-scrim"
        />
      )}

      <aside
        className={`w-64 bg-slate-900 text-slate-200 flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 md:translate-x-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="driver-sidebar"
      >
        <button
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden absolute top-3 right-3 h-8 w-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center"
          aria-label="Close menu"
          data-testid="driver-mobile-menu-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center">
              <Send className="h-4 w-4 text-white -rotate-12" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-semibold text-white text-sm leading-tight">ShipitNow</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Driver</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`
              }>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={() => nav('/')} className="w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to app
          </button>
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800">
            <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{driver?.name || user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{driver?.vehicle_type} · {driver?.vehicle_plate}</div>
            </div>
            <button onClick={() => { logout(); nav('/login'); }} className="text-slate-400 hover:text-rose-400" aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 md:ml-64">
        <main className="p-4 sm:p-6 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
};

export default DriverLayout;
